import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { BarChartRounded, TableRowsOutlined } from "@mui/icons-material";
import { BarChart } from "@mui/x-charts/BarChart";
import { AxisConfig } from "@mui/x-charts/models";
import color from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ActivitySource } from "../../Models/dashboard";
import { useDashboardActivity } from "../../Query/useDashboard";
import DashboardCard, { EmptyState } from "./DashboardCard";
import { useDashboardColors } from "./dashboardColors";

// Categorical slots 1-3 of the validated data-viz palette (colour-blind safe
// as adjacent stack segments). Each source keeps its colour whatever is shown.
const SERIES_COLORS: Record<ActivitySource, string> = {
  chat: "#2a78d6",
  evaluation: "#eb6834",
  voices: "#1baf7a",
};

const RANGES = [7, 30, 90];

interface ActivityCardProps {
  // Sources of the modules the user has; others show only if they have data.
  sources: ActivitySource[];
}

const parseDay = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const ActivityCard: React.FC<ActivityCardProps> = ({ sources }) => {
  const { t, i18n } = useTranslation("home");
  const { colors: workspace } = useWorkspaceColors();
  const { accent, onAccent } = useDashboardColors();
  const [days, setDays] = useState(30);
  const [showTable, setShowTable] = useState(false);
  const { data, isLoading, isFetching } = useDashboardActivity(days);

  const totals = data?.totals;
  const series = (Object.keys(SERIES_COLORS) as ActivitySource[]).filter(
    (source) => sources.includes(source) || (totals?.[source] ?? 0) > 0,
  );
  const hasActivity = series.some((source) => (totals?.[source] ?? 0) > 0);

  const dayLabel = (value: string, long = false) =>
    parseDay(value).toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "short",
      ...(long ? { weekday: "short" } : {}),
    });

  const dayList = data?.days ?? [];
  const labelStep = Math.max(1, Math.ceil(dayList.length / 7));
  const activeDays = dayList
    .filter((day) => series.some((source) => day[source] > 0))
    .reverse();

  // Typed as a band axis: the generic AxisConfig of x-charts v6 drops the
  // band-only options such as categoryGapRatio.
  const dayAxis: AxisConfig<"band", string> = {
    id: "days",
    scaleType: "band",
    data: dayList.map((day) => day.date),
    valueFormatter: (value: string) => dayLabel(value),
    // Label every n-th day, counted back from today so today is labelled.
    tickInterval: (_value: string, index: number) =>
      (dayList.length - 1 - index) % labelStep === 0,
    tickLabelStyle: { fontSize: 11 },
    categoryGapRatio: days > 30 ? 0.2 : 0.35,
  };

  const rangeControl = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box
        role="radiogroup"
        aria-label={t("hub.activity.range")}
        sx={{
          display: "inline-flex",
          p: 0.25,
          borderRadius: "10px",
          backgroundColor: color.gray100,
          border: `1px solid ${color.gray200}`,
        }}
      >
        {RANGES.map((range) => {
          const selected = range === days;
          return (
            <Box
              key={range}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setDays(range)}
              sx={{
                px: 1.25,
                py: 0.5,
                border: 0,
                borderRadius: "8px",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 600,
                color: selected ? onAccent : color.gray600,
                backgroundColor: selected ? accent : "transparent",
                "&:focus-visible": {
                  outline: `2px solid ${workspace.primary_dark}`,
                  outlineOffset: 1,
                },
              }}
            >
              {t("hub.activity.days", { count: range })}
            </Box>
          );
        })}
      </Box>
      <Tooltip
        title={
          showTable ? t("hub.activity.showChart") : t("hub.activity.showTable")
        }
        arrow
      >
        <IconButton
          size="small"
          onClick={() => setShowTable((value) => !value)}
          aria-label={
            showTable
              ? t("hub.activity.showChart")
              : t("hub.activity.showTable")
          }
          sx={{ color: color.gray600 }}
        >
          {showTable ? (
            <BarChartRounded sx={{ fontSize: 20 }} />
          ) : (
            <TableRowsOutlined sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <DashboardCard
      title={t("hub.activity.title")}
      subtitle={t("hub.activity.subtitle")}
      action={rangeControl}
    >
      {isLoading || !data ? (
        <Skeleton variant="rounded" height={300} />
      ) : (
        <Box
          sx={{
            opacity: isFetching ? 0.6 : 1,
            transition: "opacity 0.15s ease",
          }}
        >
          {/* Totals double as the legend. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${series.length}, minmax(0, 1fr))`,
              gap: 1.5,
              mb: 2,
            }}
          >
            {series.map((source) => (
              <Box
                key={source}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  backgroundColor: color.gray50,
                  border: `1px solid ${color.gray100}`,
                  minWidth: 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 10,
                      height: 10,
                      flexShrink: 0,
                      borderRadius: "3px",
                      backgroundColor: SERIES_COLORS[source],
                    }}
                  />
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 12,
                      lineHeight: "16px",
                      color: color.gray600,
                    }}
                  >
                    {t(`hub.activity.series.${source}`)}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: 22,
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: color.gray900,
                  }}
                >
                  {totals?.[source] ?? 0}
                </Typography>
              </Box>
            ))}
          </Box>

          {!hasActivity ? (
            <EmptyState>{t("hub.activity.empty", { count: days })}</EmptyState>
          ) : showTable ? (
            <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  "& th, & td": {
                    px: 1,
                    py: 0.75,
                    textAlign: "right",
                    borderBottom: `1px solid ${color.gray100}`,
                    fontVariantNumeric: "tabular-nums",
                  },
                  "& th:first-of-type, & td:first-of-type": {
                    textAlign: "left",
                  },
                  "& th": {
                    position: "sticky",
                    top: 0,
                    backgroundColor: color.white,
                    fontSize: 12,
                    fontWeight: 600,
                    color: color.gray500,
                  },
                  "& td": { color: color.gray700 },
                }}
              >
                <thead>
                  <tr>
                    <th>{t("hub.activity.date")}</th>
                    {series.map((source) => (
                      <th key={source}>{t(`hub.activity.series.${source}`)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeDays.map((day) => (
                    <tr key={day.date}>
                      <td>{dayLabel(day.date, true)}</td>
                      {series.map((source) => (
                        <td key={source}>{day[source]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                mx: -1,
                "& .MuiBarElement-root": {
                  // A thin surface-coloured edge keeps stacked segments apart.
                  stroke: color.white,
                  strokeWidth: 1.5,
                },
                "& .MuiChartsAxis-tickLabel": {
                  fill: `${color.gray500} !important`,
                },
                "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": {
                  stroke: `${color.gray200} !important`,
                },
                "& .MuiChartsAxisHighlight-root": {
                  fill: alpha(workspace.primary_dark, 0.06),
                },
              }}
            >
              <BarChart
                height={240}
                margin={{ top: 8, right: 8, bottom: 28, left: 36 }}
                slotProps={{ legend: { hidden: true } }}
                tooltip={{ trigger: "axis" }}
                xAxis={[dayAxis]}
                yAxis={[
                  {
                    tickMinStep: 1,
                    tickNumber: 4,
                    disableLine: true,
                    disableTicks: true,
                    tickLabelStyle: { fontSize: 11 },
                  },
                ]}
                series={series.map((source) => ({
                  id: source,
                  label: t(`hub.activity.series.${source}`),
                  data: dayList.map((day) => day[source]),
                  stack: "total",
                  color: SERIES_COLORS[source],
                }))}
              />
            </Box>
          )}
        </Box>
      )}
    </DashboardCard>
  );
};

export default ActivityCard;
